import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import VisibilityIcon from "@mui/icons-material/Visibility";
import { Box, Button, Card, CircularProgress, Fade, IconButton, Tooltip, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { useSelectedEntity } from "../../hooks/useSelectedEntity";
import { getEntityQueryKey } from "../../api/entity";
import { useLayerActions } from "../../store/layer";
import { useMapActions, useMapStore } from "../../store/map";
import { isAlreadyFramedDataset } from "../../utilities/mapBounds";
import { getSpatialDataset } from "../../services/spatialDatasetService";
import { toLayerConfig } from "../../hooks/useSpatialDatasetMapLayers";
import { spatialDatasetLayerId } from "../../constants/spatialDataset";
import { useSelectionActions, useSelectionSelector, useSelectionStore } from "../../store/selection";
import { isCmTableEndpoint } from "../../utilities/cmTableEndpoint";
import { useSpinner } from "../../contexts/SpinnerContext";
import {
  ApiEndpoint,
  EntityItem,
  MediaItem,
  MediaItemDetailed,
  MediaResponse,
  RelatedEntityItem,
  RelationshipsResponse,
  SpatialDatasetType
} from "../../types";
import { FILE_EXTENSIONS } from "../../utilities/mediaTypes";
import { constructMediaUrl, getSafeThumbnailUrl, getThumbnailIcon, isPreviewable } from "../../utilities/mediaUtils";
import {
  isImageUrl,
  getDataItemName,
  getDataItemDescription,
  getDataItemPrimaryImage,
  getDataSourceType,
  fetchItem,
  openItemForEditing
} from "../../utilities/dataSourceAdapters";
import { hasValidGeometry } from "../../utilities/geoUtils";
import { entityIsMedia } from "../../utilities/entityModalUtils";
import { mapRelationshipCategoryToTableName } from "../../utilities/relationshipCategoryEndpoint";
import { useAuthSelector } from "../../store/auth";
import { usePopupActions } from "../../store/popupStore";
import { idsEqual } from "../../utilities/idUtils";
import { FulcrumImage } from "../common/FulcrumImage";
import RelatedItemsRing from "./RelatedItemsRing";
import { getEntityColor } from "../../utilities/dataItemUtils";
// Adapter Pattern support for external data sources
import { useIsEndpointEditable } from "../../hooks/useDataSourceAdapter";
import useAuth from "../../hooks/useAuth";
import { CreateOutlined } from "@mui/icons-material";
import LayerSharePopover from "../share/LayerSharePopover";
import LinkOutlined from "@mui/icons-material/LinkOutlined";
import ErrorBoundary from "../common/ErrorBoundary";
import MediaViewer from "../media/MediaViewer";
import { useEntityFormActions } from "../../store/entityForm";
import Image from "../common/Image";
import AdditionalDetailsPanel from "./AdditionalDetailsPanel";
import FlashyButton from "../common/FlashyButton";
import { getCmTableSlugFromEndpoint } from "../../utilities/cmTableEndpoint";
// Add debug prefix for DataItemDetails debugging
const DEBUG_PREFIX = "[🖼️ DETAILS-DEBUG]";

// UI Constants for maintainability
const UI_CONSTANTS = {
  BUTTON_SPACER_HEIGHT: "50px",
  IMAGE_HEIGHT: "10rem",
  POPUP_MEDIA_HEIGHT: "10rem",
  WIDTH: {
    POPUP: "20rem",
    /** Popup with info/details open — ~50% wider than compact popup for tabular fields */
    POPUP_WITH_INFO: "30rem",
    NORMAL: "fit-content"
  },
  LIGHT_TEXT: "#FCFCFD",
  SECONDARY_TEXT: "#D0D5DD",
  MUTED_TEXT: "#98A2B3",
  CARD_BORDER_RADIUS: "0.5rem",
  SEPARATOR_BORDER: "1px solid rgba(0, 0, 0, 0.08)",
  BACKGROUND_COLOR: {
    POPUP: "#475467",
    NORMAL: "rgba(255, 255, 255, 0.95)"
  }
} as const;

const formatDate = (dateStr: string | undefined): string => {
  if (!dateStr) return "N/A";

  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

/**
 * Helper function to get consistent background styling based on popup mode
 * Follows DRY principle by centralizing background logic
 * @param popupMode - Whether the component is in popup mode (defaults to false if undefined)
 * @returns The appropriate background CSS value
 */
const getCardBackground = (popupMode?: boolean): string => {
  return popupMode ? UI_CONSTANTS.BACKGROUND_COLOR.POPUP : UI_CONSTANTS.BACKGROUND_COLOR.NORMAL;
};

const getCardWidth = (popupMode?: boolean, infoOpen?: boolean): string => {
  if (!popupMode) return UI_CONSTANTS.WIDTH.NORMAL;
  return infoOpen ? UI_CONSTANTS.WIDTH.POPUP_WITH_INFO : UI_CONSTANTS.WIDTH.POPUP;
};

interface DataItemDetailsProps {
  item: MediaItem | EntityItem;
  onBack: () => void;
  dataDrawerEndpoint: ApiEndpoint | null;
  dataItemEndpoint: ApiEndpoint | null;
  onItemUpdate?: (updatedItem: MediaItem | EntityItem) => void;
  onItemDelete?: (itemId: number | string) => void;
  onOpenBulkEdit?: (itemIds: string[]) => void;
  isEditingDisabled?: boolean;
  availableHeight?: string;
  popupMode?: boolean;
  onShowRelatedItems?: () => void; // Callback to trigger related items mode
  hideRelatedItemsRing?: boolean; // General prop to hide RelatedItemsRing for various use cases
}

// Update type guard
const isMediaItem = (item: any): item is MediaItem => {
  return "url" in item && "thumbnail" in item && "media_type" in item;
};

const DataItemDetails: React.FC<DataItemDetailsProps> = React.memo(
  ({
    item: initialItem,
    onBack,
    dataDrawerEndpoint,
    dataItemEndpoint,
    onItemUpdate,
    onItemDelete,
    onOpenBulkEdit,
    isEditingDisabled,
    availableHeight,
    popupMode,
    onShowRelatedItems,
    hideRelatedItemsRing
  }): JSX.Element => {
    // PERFORMANCE MONITORING: Track prop changes to identify re-render causes
    const propChangesRef = useRef<any>({
      initialItem: initialItem,
      onBack: onBack,
      dataDrawerEndpoint: dataDrawerEndpoint,
      dataItemEndpoint: dataItemEndpoint,
      onItemUpdate: onItemUpdate,
      onItemDelete: onItemDelete,
      onOpenBulkEdit: onOpenBulkEdit,
      isEditingDisabled: isEditingDisabled,
      availableHeight: availableHeight,
      popupMode: popupMode
    });

    useEffect(() => {
      const prevProps = propChangesRef.current;
      const currentProps = {
        initialItem: initialItem,
        onBack: onBack,
        dataDrawerEndpoint: dataDrawerEndpoint,
        dataItemEndpoint: dataItemEndpoint,
        onItemUpdate: onItemUpdate,
        onItemDelete: onItemDelete,
        onOpenBulkEdit: onOpenBulkEdit,
        isEditingDisabled: isEditingDisabled,
        availableHeight: availableHeight,
        popupMode: popupMode
      };

      const changedProps: string[] = [];
      (Object.keys(currentProps) as Array<keyof typeof currentProps>).forEach((key) => {
        if (prevProps[key] !== currentProps[key]) {
          changedProps.push(key);
        }
      });

      if (changedProps.length > 0) {
        console.log(`🔍 [DataItemDetails] PERF: Props changed:`, {
          changedProps: changedProps,
          itemId: initialItem?.id,
          itemName: initialItem?.name,
          prevAvailableHeight: prevProps.availableHeight,
          currentAvailableHeight: availableHeight,
          functionChanges: {
            onBack: prevProps.onBack !== onBack,
            onItemUpdate: prevProps.onItemUpdate !== onItemUpdate,
            onItemDelete: prevProps.onItemDelete !== onItemDelete,
            onOpenBulkEdit: prevProps.onOpenBulkEdit !== onOpenBulkEdit
          }
        });
      }

      propChangesRef.current = currentProps;
    });

    const config = useConfig();
    const { enqueueSnackbar } = useSnackbar();
    const { canUpdate, canDelete } = useAuth();
    const [item, setItem] = useState(initialItem);
    const [activeEndpoint, setActiveEndpoint] = useState<ApiEndpoint | null>(dataItemEndpoint);
    const [loadingRels, setLoadingRels] = useState(false);

    // Preview states
    const [previewItem, setPreviewItem] = useState<MediaResponse | MediaItemDetailed | null>(null);
    const { loading, startLoading, stopLoading } = useSpinner();
    const [isChangingItem, setIsChangingItem] = useState(false);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [thumbnailError, setThumbnailError] = useState(false);
    const [fullImageError, setFullImageError] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    // The description tooltip opens upward, over the Find related / Edit buttons. Controlling it
    // here lets the dismiss button inside the tooltip get it out of the way without the user
    // having to first move the cursor off the description.
    const [isDescriptionTooltipOpen, setIsDescriptionTooltipOpen] = useState(false);

    // const latestRequestRef = useRef<number | null>(null); // unused
    const ringContainerRef = useRef<HTMLDivElement>(null);
    const geoPopupCardRef = useRef<HTMLDivElement | null>(null);
    const [ringContainerSize, setRingContainerSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

    const {
      entity: selectedEntity,
      endpoint: selectedEndpoint,
      isFetchingRelationships,
      isError: isRelationshipsLoadError,
      fetchRelationships,
      refresh: refreshSelectedEntity
    } = useSelectedEntity();
    const { setSelectedPrimaryStates, setShowRelationship } = useSelectionActions();
    const { showRelationShip } = useSelectionSelector();
    const queryClient = useQueryClient();
    const { user: userProfile } = useAuthSelector();
    const { openGeoPopup, openMediaViewer, openNonGeoPopup, bumpGeoPopupLayoutRevision, closePopup, openFullMediaViewer } =
      usePopupActions();

    useLayoutEffect(() => {
      if (!popupMode) return;
      bumpGeoPopupLayoutRevision();
    }, [popupMode, showInfo, showRelationShip, item?.id, bumpGeoPopupLayoutRevision]);

    useLayoutEffect(() => {
      if (!popupMode) return;
      const el = geoPopupCardRef.current;
      if (!el) return;
      let raf = 0;
      const ro = new ResizeObserver(() => {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(() => {
          bumpGeoPopupLayoutRevision();
        });
      });
      ro.observe(el);
      return () => {
        cancelAnimationFrame(raf);
        ro.disconnect();
      };
    }, [popupMode, bumpGeoPopupLayoutRevision]);

    const { openEditForm } = useEntityFormActions();
    const { refreshLayer, mergeSpatialDatasetLayers, setLayerVisibility } = useLayerActions();
    const { setCommonMapState } = useMapActions();
    /** Which dataset the map already shows — see the framing rule in SideBar. */
    const activeSpatialDatasetEntityId = useMapStore((s) => s.activeSpatialDatasetEntityId);

    // Alias so existing references below compile without renaming every occurrence
    const loadingProgress = isFetchingRelationships ? { step: "Loading relationships...", current: 1, total: 1 } : null;

    // Add debug logging for context and prop changes
    useEffect(() => {
      console.log("[DataItemDetails] Props/Context changed:", {
        initialItemId: initialItem?.id,
        initialItemName: initialItem?.name,
        currentItemId: item?.id,
        currentItemName: item?.name,
        activeEndpointName: activeEndpoint?.name,
        activeEndpointTableName: activeEndpoint?.tableName,
        selectedEntityId: selectedEntity?.id,
        selectedEntityName: selectedEntity?.name,
        selectedEntityType: selectedEntity?.entityType,
        selectedEntityIsComplete: selectedEntity?.isDataLoaded,
        popupMode
      });
    }, [initialItem, item, activeEndpoint, selectedEntity, popupMode]);

    // Check if this is a Fulcrum photo that shouldn't be displayed as standalone
    useEffect(() => {
      if (initialItem) {
        const itemData = { ...initialItem, ...(initialItem as any).properties };
        const dataSourceType = getDataSourceType(itemData, dataItemEndpoint);

        const isFulcrumPhoto = !!(
          (itemData as any).fulcrum_photo?.photo_id ||
          (itemData as any).fulcrum_data?.form_id ||
          (itemData as any)._source?.type === "fulcrum" ||
          dataSourceType === "fulcrum"
        );

        const isMediaItem =
          (initialItem as any).media_type || (initialItem as any).entity_thumbnail || dataItemEndpoint?.tableName === "media";

        if (isMediaItem && isFulcrumPhoto) {
          console.log("[DataItemDetails] Fulcrum photo detected - redirecting back to list");
          enqueueSnackbar("This item does not have its own properties", {
            variant: "info"
          });
          onBack();
          return;
        }
      }
    }, [initialItem, dataItemEndpoint, onBack, enqueueSnackbar]);

    // Simplified: Only sync when we get a different item from props
    useEffect(() => {
      if ((initialItem && (!item || item.id !== initialItem.id)) || item?.thumbnail !== initialItem?.thumbnail) {
        setItem((prev) => ({ ...prev, ...initialItem, thumbnail: initialItem?.thumbnail || prev.thumbnail }));
        setActiveEndpoint(dataItemEndpoint);
      }
    }, [initialItem, dataItemEndpoint, item?.id, item?.thumbnail]);

    // CRITICAL: Update activeEndpoint when dataItemEndpoint changes
    useEffect(() => {
      if (dataItemEndpoint && dataItemEndpoint.tableName !== activeEndpoint?.tableName) {
        setActiveEndpoint(dataItemEndpoint);
      }
    }, [dataItemEndpoint, activeEndpoint, item?.id, item?.name]);

    async function handleEdit() {
      if (!item || !selectedEndpoint) return;

      // Let the adapter handle data-source-specific editing (e.g. Fulcrum → web interface).
      // If it returns true, external editing was initiated and we're done.
      if (openItemForEditing(item, selectedEndpoint)) return;

      // Close the popup Coordinates or primary image may change during edit, so the popup position would be stale.
      const isCmTable = isCmTableEndpoint(selectedEndpoint);
      if (isCmTable) closePopup();

      openEditForm(selectedEndpoint, item as MediaItem, {
        async onUpdated(updatedItem) {
          // Update local state immediately for responsive UI
          setItem((prev) => ({ ...prev, ...updatedItem }));

          if (selectedEntity) {
            try {
              console.log(`[DataItemDetails] Refreshing selected entity after update of ${updatedItem?.name || item?.id}`);
              await refreshSelectedEntity();
              setItem((prev) => ({ ...prev, ...updatedItem }));

              // Trigger GeoJSON refetch to update map markers/popups
              const layerId = selectedEndpoint?.mapLayerId || selectedEndpoint?.tableName;
              if (layerId) {
                console.log(`[DataItemDetails] Triggering refresh for layer: ${layerId}`);
                refreshLayer(layerId);
              }

              // Reopen the popup we closed before editing. Coords are omitted so
              // MapComponent derives them from the (now-refreshed) entity data —
              // panning to the updated location if the geometry changed.
              if (isCmTable) {
                setTimeout(() => openGeoPopup(), 750);
              }
            } catch (error) {
              console.error("[DataItemDetails] Error refreshing entity data after edit:", error);
            }
          }
        },

        onCancel(canceledItem) {
          // try to sync thumbnail if current thumbnail do not match canceled thumbnail
          if (!item?.thumbnail || item.thumbnail !== canceledItem?.thumbnail) {
            setItem((prev) => ({ ...prev, thumbnail: canceledItem.thumbnail }));
          }
        }
      });
    }

    // Fallback to primary image
    // Use generic adapter function to get primary image
    const primaryImageUrl = getDataItemPrimaryImage(item);
    const originalImageUrl = primaryImageUrl && isImageUrl(primaryImageUrl) ? constructMediaUrl(primaryImageUrl) : null;
    // Get thumbnail directly from item.thumbnail field

    let thumbnailUrl = item?.thumbnail;

    // Use fallback thumbnail sources for all data sources via generic adapter functions
    // Get image and thumbnail URLs using generalized functions
    // Enhanced thumbnail detection using adapter pattern
    const enhancedThumbnailUrl = getSafeThumbnailUrl(item, dataItemEndpoint);
    if (enhancedThumbnailUrl) thumbnailUrl = enhancedThumbnailUrl;

    // Check thumbnailUrl as secondary source
    const thumbnailFromPrimaryImage = (item as any)?.thumbnailUrl || (item as any)?.primary_image?.thumbnail;
    if (thumbnailFromPrimaryImage) {
      thumbnailUrl = thumbnailFromPrimaryImage;
    }

    // Determine if we should show any media area at all
    const shouldShowMediaArea = Boolean(thumbnailUrl || originalImageUrl) && Boolean(!thumbnailError && !fullImageError);

    // Show fallback only for media items that have a URL but no thumbnail/image
    const shouldShowFallback = isMediaItem(item) && item.url && isPreviewable(item.url) && !shouldShowMediaArea;

    const renderMediaArea = () => {
      console.log(`${DEBUG_PREFIX} Rendering media area:`, {
        shouldShowMediaArea,
        shouldShowFallback,
        thumbnailUrl,
        originalImageUrl
      });

      // No primary image and nothing previewable — skip the empty media block
      // so the card opens on the title, matching spatial feature info.
      if (!shouldShowMediaArea && !shouldShowFallback) {
        console.log(`${DEBUG_PREFIX} No media to display`);
        return null;
      }

      // Helper function to create the click to view banner for media items
      const renderClickToViewBanner = () => {
        if (!isMediaItem(item) || !item.url || !isPreviewable(item.url)) return null;

        return (
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              maxWidth: 140,
              // Enhanced glass morphism banner
              background: "rgba(0, 0, 0, 0.75)",
              backdropFilter: "blur(8px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              borderRadius: 2,
              p: "0.5em",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 1.5,
              zIndex: 2,

              // Enhanced shadow and transitions
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              transition: "all 0.2s ease-in-out",

              "&:hover": {
                background: "rgba(0, 0, 0, 0.85)",
                boxShadow: "0 6px 25px rgba(0, 0, 0, 0.4)"
              }
            }}
          >
            <VisibilityIcon
              sx={{
                fontSize: "1rem",
                mr: 8,
                color: "white",
                filter: "drop-shadow(0 1px 2px rgba(0, 0, 0, 0.3))"
              }}
            />
            <Typography
              variant='body2'
              sx={{
                fontWeight: 500,
                color: "white",
                letterSpacing: "0.02em",
                textShadow: "0 1px 2px rgba(0, 0, 0, 0.3)"
              }}
            >
              Click to View
            </Typography>
          </Box>
        );
      };

      if (shouldShowFallback) {
        console.log(`${DEBUG_PREFIX} Showing fallback media area`);
        return (
          <Box
            sx={{
              position: "relative",
              height: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "140px",
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",

              // Modern gradient background
              background: `linear-gradient(135deg,
              ${getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")}10 0%,
              ${getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")}05 100%)`,

              // Enhanced border
              border: `2px dashed ${getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")}60`,
              borderRadius: 2,

              cursor: "pointer",
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

              "&:hover": {
                background: `linear-gradient(135deg,
                ${getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")}20 0%,
                ${getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")}10 100%)`,
                borderColor: getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related"),
                transform: "translateY(-1px)",
                boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)"
              }
            }}
            onClick={() => {
              if (isMediaItem(item) && item.url) {
                if (popupMode) {
                  openFullMediaViewer();
                } else {
                  setPreviewItem(item as unknown as MediaResponse);
                  setPreviewUrl(item.url);
                  setIsPreviewOpen(true);
                }
              }
            }}
          >
            {/* try to show thumbnail again if primary renderer failed, if this is also failed show processing status */}
            {thumbnailUrl && (
              <Image
                src={thumbnailUrl}
                height={popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : 140}
                alt={"media thumbnail"}
                width={"100%"}
                errorComponent={
                  <Box
                    sx={{
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.700",
                      inset: 0,
                      zIndex: 9999
                    }}
                  >
                    <Typography variant='body1' color='common.white'>
                      Processing
                    </Typography>
                  </Box>
                }
              />
            )}
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                height: "100%",
                width: "100%",
                alignItems: "center",
                justifyContent: "center"
              }}
            >
              <img
                src={getThumbnailIcon(item.url || "")}
                alt='Media icon'
                style={{
                  height: 40,
                  display: "block",
                  opacity: 0.8,
                  filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))"
                }}
              />
              <Typography
                variant='body2'
                sx={{
                  bgcolor: "",
                  color: "text.primary",
                  fontWeight: 600,
                  textAlign: "center",
                  fontSize: "0.95rem",
                  letterSpacing: "0.02em"
                }}
              >
                {(() => {
                  const mediaType = getMediaType(item as any);
                  switch (mediaType) {
                    case "video":
                      return "Click to view video";
                    case "audio":
                      return "Click to view audio file";
                    case "pdf":
                      return "Click to view PDF document";
                    case "document":
                      return "Click to view document";
                    case "image":
                      return "Click to view image";
                    default:
                      return "Click to view file";
                  }
                })()}
              </Typography>
            </Box>
          </Box>
        );
      }

      if (thumbnailUrl && !thumbnailError) {
        console.log(`${DEBUG_PREFIX} Showing thumbnail: ${thumbnailUrl}`);
        return (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              cursor: isMediaItem(item) && getMediaUrl(item) ? "pointer" : "default"
            }}
            id='map-popup-thumbnail'
            onClick={() => {
              if (isMediaItem(item)) {
                const mediaUrl = getMediaUrl(item);
                if (mediaUrl) {
                  if (popupMode) {
                    openFullMediaViewer();
                  } else {
                    setPreviewItem(item as unknown as MediaResponse);
                    setPreviewUrl(mediaUrl);
                    setIsPreviewOpen(true);
                  }
                }
              }
            }}
          >
            <FulcrumImage
              src={thumbnailUrl}
              alt={item.name}
              region='AU'
              thumbnail={true}
              sx={{
                width: "100%",
                minHeight: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "8.75rem",
                maxHeight: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "16.75rem", // Keep popup media height stable
                height: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "auto",
                display: "block",
                objectFit: "cover",
                aspectRatio: "auto",
                borderRadius: "12px 12px 0 0", // Rounded top corners only
                transition: "transform 0.3s ease-in-out"
              }}
              onError={handleThumbnailError}
            />
            <Box className='click-to-view-banner' sx={{ opacity: 1, transition: "opacity 0.2s ease-in-out" }}>
              {renderClickToViewBanner()}
            </Box>
          </Box>
        );
      }

      if (originalImageUrl && !fullImageError) {
        console.log(`${DEBUG_PREFIX} Showing full image: ${originalImageUrl}`);
        return (
          <Box
            sx={{
              position: "relative",
              width: "100%",
              cursor: isMediaItem(item) && getMediaUrl(item) ? "pointer" : "default"
            }}
            id='map-popup-thumbnail'
            onClick={() => {
              if (isMediaItem(item)) {
                const mediaUrl = getMediaUrl(item);
                if (mediaUrl) {
                  if (popupMode) {
                    openFullMediaViewer();
                  } else {
                    setPreviewItem(item as unknown as MediaResponse);
                    setPreviewUrl(mediaUrl);
                    setIsPreviewOpen(true);
                  }
                }
              }
            }}
          >
            <FulcrumImage
              src={originalImageUrl}
              alt={item.name}
              region='AU'
              thumbnail={false}
              sx={{
                width: "100%",
                minHeight: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "8.75rem",
                maxHeight: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "16.75rem", // Keep popup media height stable
                height: popupMode ? UI_CONSTANTS.POPUP_MEDIA_HEIGHT : "auto",
                display: "block",
                objectFit: "cover",
                borderRadius: "12px 12px 0 0", // Rounded top corners only
                transition: "transform 0.3s ease-in-out"
              }}
              onError={handleFullImageError}
            />
            <Box className='click-to-view-banner' sx={{ opacity: 1, transition: "opacity 0.2s ease-in-out" }}>
              {renderClickToViewBanner()}
            </Box>
          </Box>
        );
      }

      // If we reach here, don't show any media area
      console.log(`${DEBUG_PREFIX} No valid media to display`);
      return null;
    };

    useEffect(() => {
      // Function to update size
      const updateSize = () => {
        if (ringContainerRef.current) {
          const padding = 32;
          const width = ringContainerRef.current.clientWidth - padding;
          const height = ringContainerRef.current.clientHeight - padding;

          console.log("[DataItemDetails] 📏 Measuring container size:", {
            clientWidth: ringContainerRef.current.clientWidth,
            clientHeight: ringContainerRef.current.clientHeight,
            padding,
            finalWidth: width,
            finalHeight: height
          });

          setRingContainerSize({ width, height });
        } else {
          console.log("[DataItemDetails] 📏 Container ref not available for measurement");
        }
      };

      // Initial measurement with delay to allow layout
      const timeoutId = setTimeout(() => {
        updateSize();
      }, 100); // Small delay to allow container to be laid out

      // Measure on window resize
      window.addEventListener("resize", updateSize);

      // Cleanup listener and timeout
      return () => {
        window.removeEventListener("resize", updateSize);
        clearTimeout(timeoutId);
      };
    }, []);

    // Re-measure ring container when relationships load in (L3 data arrives)
    useEffect(() => {
      if (selectedEntity?.relationships) {
        console.log("[DataItemDetails] 📏 Relationships loaded, re-measuring container...");
        const updateSize = () => {
          if (ringContainerRef.current) {
            const padding = 32;
            const width = ringContainerRef.current.clientWidth - padding;
            const height = ringContainerRef.current.clientHeight - padding;
            setRingContainerSize({ width, height });
          }
        };
        const timeoutId = setTimeout(updateSize, 50);
        return () => clearTimeout(timeoutId);
      }
    }, [selectedEntity?.relationships]);

    // Sync React Query entity data (L2 + L3) into local item state for display
    useEffect(() => {
      if (selectedEntity && String(selectedEntity.id) === String(item?.id) && selectedEntity.properties) {
        const queryEntityTimestamp = new Date(selectedEntity.properties.last_updated_at || 0).getTime();
        const localItemTimestamp = new Date(item?.last_updated_at || 0).getTime();

        // Only overwrite local state if the query has newer data or relationships newly loaded
        if (queryEntityTimestamp > localItemTimestamp || (!item?.relationships && selectedEntity.relationships)) {
          setItem({
            ...selectedEntity.properties,
            // Pull safe scalar fields from the entity level (adapter-computed values).
            // Avoid spreading ...selectedEntity — it carries nested objects (fulcrum_data,
            // geometry, properties) that can crash React if rendered as children.
            id: selectedEntity.id,
            name: selectedEntity.name,
            description: selectedEntity.description,
            thumbnail: selectedEntity.thumbnail,
            created_at: selectedEntity.created_at,
            last_updated_at: selectedEntity.last_updated_at,
            relationships: selectedEntity.relationships
          } as MediaItem | EntityItem);
        }
      }
    }, [selectedEntity?.relationships, selectedEntity?.properties, item?.id]);

    const hasRelationships = (relationships: any): boolean => {
      console.log(`${DEBUG_PREFIX} hasRelationships called with:`, {
        relationships,
        hasRelationships: !!relationships,
        keysFound: relationships ? Object.keys(relationships) : [],
        relatedEntitiesKeys: relationships?.related_entities ? Object.keys(relationships.related_entities) : [],
        geoEntitiesKeys: relationships?.geo_entities ? Object.keys(relationships.geo_entities) : [],
        mediaLength: Array.isArray(relationships?.media) ? relationships.media.length : 0
      });

      if (!relationships) return false;

      // Check normalized structure
      if (relationships.byTableSlug) {
        return (
          Object.values(relationships.byTableSlug).some((arr: any) => arr.length > 0) ||
          (Array.isArray(relationships.media) && relationships.media.length > 0)
        );
      }

      // Fallback for legacy bucketed data
      const hasRelated =
        relationships.related_entities &&
        Object.values(relationships.related_entities).some((arr: any) => Array.isArray(arr) && arr.length > 0);
      const hasGeo =
        relationships.geo_entities && Object.values(relationships.geo_entities).some((arr: any) => Array.isArray(arr) && arr.length > 0);
      const hasMedia = Array.isArray(relationships.media) && relationships.media.length > 0;

      return !!(hasRelated || hasGeo || hasMedia);
    };

    // Helper function to check if an item has potential relationships that can be fetched
    const hasPotentialRelationships = (item: any): boolean => {
      // For Fulcrum items, check if they have form values that might contain linked records
      if (item?.dataSourceType === "fulcrum" || item?.fulcrum_data) {
        const formValues = item?.fulcrum_data?.form_values || item?.form_values;
        if (formValues && Object.keys(formValues).length > 0) {
          console.log(`${DEBUG_PREFIX} hasPotentialRelationships returning true - Fulcrum item with form values`);
          return true;
        }
      }

      // For Django items, they might have relationships if they exist in the system
      if (item?.dataSourceType === "django" || (!item?.dataSourceType && !item?.fulcrum_data)) {
        console.log(`${DEBUG_PREFIX} hasPotentialRelationships returning true - Django item`);
        return true;
      }

      console.log(`${DEBUG_PREFIX} hasPotentialRelationships returning false`);
      return false;
    };

    const mapCategoryToEntityType = useCallback(
      (category: string) => mapRelationshipCategoryToTableName(category, config.apiEndpoints),
      [config.apiEndpoints]
    );

    // PERFORMANCE: Memoize handleRelatedItemClick to prevent RelatedItemsRing re-renders.
    // API relationship rows are slim (no geometry). We update the store like the sidebar, prefetch
    // L2 into the same React Query key as useEntityFetch, then open the same primary popup.
    const handleRelatedItemClick = useCallback(
      async (entity: RelatedEntityItem, category: string) => {
        const mappedEntityType = mapCategoryToEntityType(category);

        const targetEndpoint = config.apiEndpoints.find((endpoint) => endpoint.tableName === mappedEntityType);

        const entityName = entity.entity_name || entity.name || `${targetEndpoint?.name || category} Item`;

        console.log("[DataItemDetails] Related item click - updating selectionStore:", {
          entityId: entity.id,
          entityName: entityName,
          mappedEntityType,
          targetEndpoint: targetEndpoint?.tableName
        });

        if (!targetEndpoint) {
          console.error("[DataItemDetails] No endpoint found for entity type:", mappedEntityType);
          return;
        }

        setIsChangingItem(true);

        setSelectedPrimaryStates({
          selectedEntityId: entity.id,
          selectedEndpoint: targetEndpoint,
          showRelationShip: false
        });

        // Spatial datasets are displayed as a map layer + corner card, not a popup pin.
        // Mirror the SideBar.handleSelectEntity spatial-dataset path.
        if (targetEndpoint.entityType === "spatial-dataset") {
          openNonGeoPopup();
          (async () => {
            setCommonMapState({ isSpatialDatasetLoading: true });
            let flyToDispatched = false;
            try {
              const ds = await getSpatialDataset(Number(entity.id));
              if (ds.processing_status === "completed" && ds.display_url) {
                const layerConfig = toLayerConfig(ds);
                if (layerConfig) {
                  mergeSpatialDatasetLayers([layerConfig]);
                  setLayerVisibility(spatialDatasetLayerId(ds.id), true);
                }
              }
              if (ds.bbox && ds.bbox.length === 4) {
                const [minLng, minLat, maxLng, maxLat] = ds.bbox;
                window.dispatchEvent(
                  new CustomEvent("culturemap:flyTo", {
                    detail: {
                      layerId: spatialDatasetLayerId(ds.id),
                      bounds: [
                        [minLng, minLat],
                        [maxLng, maxLat]
                      ],
                      clearSpatialLoading: true,
                      // Same rule as SideBar: only a dataset already on the map
                      // can be "already framed"; a newly picked one is framed.
                      skipIfCenterWithinBounds: isAlreadyFramedDataset(activeSpatialDatasetEntityId, entity.id)
                    }
                  })
                );
                flyToDispatched = true;
              }
            } catch {
              // Non-fatal — map won't update but card still opens
            }
            if (!flyToDispatched) setCommonMapState({ isSpatialDatasetLoading: false });
            setIsChangingItem(false);
          })();
          return;
        }

        try {
          const fullEntity = await queryClient.fetchQuery({
            queryKey: getEntityQueryKey(entity.id, targetEndpoint, "L2"),
            // Match useEntityFetch L2 (entity.ts): same queryFn args so cache stays consistent
            queryFn: () => fetchItem(entity.id, targetEndpoint, userProfile)
          });

          const { primary } = useSelectionStore.getState();
          if (!idsEqual(primary.selectedEntityId, entity.id) || primary.selectedEndpoint?.tableName !== targetEndpoint.tableName) {
            return;
          }

          if (hasValidGeometry(fullEntity)) {
            openGeoPopup();
          } else if (entityIsMedia(fullEntity)) {
            openMediaViewer();
          } else {
            openNonGeoPopup();
          }
        } catch (error) {
          console.error("[DataItemDetails] Failed to load related entity for map popup:", error);
        } finally {
          setTimeout(() => {
            setIsChangingItem(false);
          }, 100);
        }
      },
      [
        activeSpatialDatasetEntityId,
        config.apiEndpoints,
        mapCategoryToEntityType,
        mergeSpatialDatasetLayers,
        openGeoPopup,
        openMediaViewer,
        openNonGeoPopup,
        queryClient,
        setCommonMapState,
        setIsChangingItem,
        setLayerVisibility,
        setSelectedPrimaryStates,
        userProfile
      ]
    );

    const transformToResponseShape = (relationships: any): RelationshipsResponse => {
      if (!relationships)
        return {
          related_entities: {},
          geo_entities: {
            place: [],
            familyarea: []
          },
          media: []
        };

      // If it's already in the new shape, return as is
      if ("related_entities" in relationships) {
        return relationships as RelationshipsResponse;
      }

      // Transform old format to new
      return {
        related_entities: Object.entries(relationships)
          .filter(([key]) => !["places", "family_areas"].includes(key))
          .reduce((acc, [key, value]) => ({ ...acc, [key]: value }), {}),
        geo_entities: {
          place: relationships.places || [],
          familyarea: relationships.family_areas || []
        },
        media: relationships.media || []
      };
    };

    // Helper function to detect media type from item
    const getMediaType = (item: MediaItem | EntityItem): string => {
      // Check for media_type property
      if (isMediaItem(item) && item.media_type) {
        return item.media_type.toLowerCase();
      }

      // Helper function to check if URL ends with any extension from a list
      const urlEndsWithAnyExtension = (url: string, extensions: string[]): boolean => {
        const lowerUrl = url.toLowerCase();
        return extensions.some((ext) => lowerUrl.endsWith(`.${ext.toLowerCase()}`));
      };

      // Handle entity items with primary image
      const entityPrimaryImageUrl = getDataItemPrimaryImage(item);
      if (!isMediaItem(item) && entityPrimaryImageUrl) {
        const url = entityPrimaryImageUrl;
        if (url.toLowerCase().endsWith(".pdf")) return "pdf";
        if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.VIDEO)) return "video";
        if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.IMAGE)) return "image";
        if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.AUDIO)) return "audio";
      }

      // Check for URL to determine type
      if (isMediaItem(item) && item.url) {
        const url = item.url;
        if (url.toLowerCase().endsWith(".pdf")) return "pdf";
        if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.VIDEO)) return "video";
        if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.IMAGE)) return "image";
        if (urlEndsWithAnyExtension(url, FILE_EXTENSIONS.AUDIO)) return "audio";
      }

      // Check remote details if available
      if (isMediaItem(item) && item.remote_details?.mime) {
        const mimeType = item.remote_details.mime.toLowerCase();
        if (mimeType.includes("pdf")) return "pdf";
        if (mimeType.includes("video")) return "video";
        if (mimeType.includes("image")) return "image";
        if (mimeType.includes("audio")) return "audio";
      }

      return "unknown";
    };

    const handleThumbnailError = () => {
      setThumbnailError(true);
    };

    const handleFullImageError = () => {
      setFullImageError(true);
    };

    // Get media URL helper
    const getMediaUrl = (item: any): string => {
      if (isMediaItem(item) && item.url) {
        return item.url;
      }
      const entityPrimaryImageUrl = getDataItemPrimaryImage(item);
      if (!isMediaItem(item) && entityPrimaryImageUrl) {
        return entityPrimaryImageUrl;
      }
      return "";
    };

    const handleClosePreview = () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewItem(null);
      setIsPreviewOpen(false);
    };

    async function handleFindRelatedItems() {
      setLoadingRels(true);
      startLoading();

      try {
        // fetchRelationships sets showRelationShip=true, fetches L3, and returns:
        //   true  → relationships found, panel stays open
        //   false → no relationships, panel auto-collapses
        const hasRelated = await fetchRelationships();
        if (!hasRelated) {
          enqueueSnackbar("No related items found for this entity.", { variant: "info" });
          return;
        }
        onShowRelatedItems?.();
      } catch (error) {
        console.error("[DataItemDetails] Error in Find Related:", error);
        enqueueSnackbar("Unable to load related items", { variant: "error" });
      } finally {
        setLoadingRels(false);
        stopLoading();
      }
    }

    const handleRelatedLinkClick = async () => {
      if (showRelationShip) {
        setShowRelationship(false);
        return;
      }
      await handleFindRelatedItems();
    };

    // Use adapter pattern to determine if endpoint is editable
    const isEndpointEditable = useIsEndpointEditable(activeEndpoint);

    // Note: DataItemDetails reads entity data from useSelectedEntity (React Query).
    // It does NOT write back to any selection state — that is the responsibility of interaction handlers.

    const entityName = getDataItemName(item, dataItemEndpoint || undefined);

    // "Find Related" visibility:
    //  - Show whenever the relationship drawer is closed (!showRelationShip) so the
    //    user can always reopen it — including after clicking "Back to grid" when
    //    alwaysRequestRelatedItemsAndMedia is on.
    //  - Also show as a retry control when the load errored and the drawer is open.
    const alwaysLoadRelationships = Boolean(config.alwaysRequestRelatedItemsAndMedia);
    const isSameSelectedEntity = Boolean(selectedEntity && item && idsEqual(selectedEntity.id, item.id));
    const relationshipsLoadInProgress = loadingRels || loading || isFetchingRelationships;
    const shouldShowFindRelatedButton = hideRelatedItemsRing
      ? false
      : !showRelationShip || (isSameSelectedEntity && isRelationshipsLoadError);
    const findRelatedTooltip = relationshipsLoadInProgress
      ? "Loading related items..."
      : alwaysLoadRelationships && isRelationshipsLoadError
        ? "Retry loading related items"
        : "Find related items";

    return (
      <ErrorBoundary
        resetKeys={[item?.id, activeEndpoint?.tableName].filter(Boolean) as (string | number)[]} // Reset error boundary when item or endpoint changes
        fallback={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              p: 3,
              textAlign: "center"
            }}
          >
            <Typography variant='h6' color='error' gutterBottom>
              Unable to load item details
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
              There was an error loading the details for this item. This may be due to slow server response or network issues.
            </Typography>
            <Button variant='outlined' onClick={onBack}>
              Go Back
            </Button>
          </Box>
        }
      >
        <Box
          sx={{
            position: "relative",
            width: getCardWidth(popupMode, showInfo),
            height: availableHeight || "100vh", // Use availableHeight if provided
            display: "flex",
            justifyContent: "center",
            alignItems: "center", // Keep centered
            p: popupMode ? 0 : 3, // Reduced padding in popup mode
            overflow: "hidden"
          }}
        >
          {/* Main card with enhanced styling and height limit */}
          <Card
            ref={geoPopupCardRef}
            sx={{
              filter: popupMode ? "none" : "drop-shadow(0 4px 12px rgba(0, 0, 0, 0.1))",
              // Clean modern design without blur (to avoid affecting photos)
              background: getCardBackground(popupMode),
              border: `2px solid ${getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")}40`, // 40 = 25% opacity
              borderRadius: UI_CONSTANTS.CARD_BORDER_RADIUS,

              // Responsive sizing with height limit
              width: popupMode ? "100%" : "fit-content",
              minWidth: popupMode ? "100%" : 280, // Increased minimum width
              maxWidth: popupMode ? "100%" : 500, // Increased maximum width
              // In popup mode the parent height is `auto`, so a percentage maxHeight can't
              // constrain growth. Use a viewport-relative cap so a long info panel scrolls
              // internally instead of expanding the popup up behind the navbar.
              maxHeight: popupMode ? "min(60vh, 560px)" : "90%",

              // Enhanced shadows and positioning
              position: "relative",
              zIndex: 1,
              boxShadow: popupMode ? "none" : "0 20px 40px -12px rgba(0, 0, 0, 0.15), 0 8px 16px -8px rgba(0, 0, 0, 0.1)",

              // Layout and overflow for scrolling
              display: "flex",
              flexDirection: "column",
              overflow: "hidden", // Hide overflow on card itself

              // Enhanced transitions and interactions
              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
            }}
          >
            {/* Loading Overlay - Enhanced glass morphism */}
            <Fade in={isChangingItem}>
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",

                  // Enhanced glass morphism overlay
                  background: "rgba(255, 255, 255, 0.85)",
                  backdropFilter: "blur(15px)",
                  border: "1px solid rgba(255, 255, 255, 0.2)",

                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 2,
                  zIndex: 10,

                  // Smooth entrance animation
                  animation: "fadeInBlur 0.3s ease-out",
                  "@keyframes fadeInBlur": {
                    "0%": {
                      opacity: 0,
                      backdropFilter: "blur(0px)"
                    },
                    "100%": {
                      opacity: 1,
                      backdropFilter: "blur(15px)"
                    }
                  }
                }}
              >
                <CircularProgress
                  size={48}
                  thickness={3}
                  sx={{
                    color: getEntityColor((selectedEntity?.entityType as "media" | "related" | "geo") || "related")
                  }}
                />
                <Typography
                  variant='body2'
                  sx={{
                    color: "text.secondary",
                    fontWeight: 500,
                    textAlign: "center"
                  }}
                >
                  Loading details...
                </Typography>
              </Box>
            </Fade>

            {/* Close Button */}
            <IconButton
              className='back-button'
              onClick={onBack}
              sx={{
                top: 8,
                right: 8,
                background: "#FCFCFD",
                width: 32,
                height: 32,
                zIndex: 10,
                position: "absolute",
                color: "#667085",
                padding: "0.625rem",
                // Initial state
                opacity: 0.6,
                borderRadius: "0.25rem",

                // Smooth transitions
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",

                "& .MuiSvgIcon-root": {
                  fontSize: "1.5rem",
                  color: "#667085"
                },

                "&:hover": {
                  background: "rgba(255, 255, 255, 0.95)",
                  opacity: 1,
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.1)"
                }
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Render Media Area using helper function */}
            {renderMediaArea()}

            {/* Content Section - Enhanced styling with scrolling */}
            <Box
              sx={{
                // Reserve space for the close chip when there is no media for it to sit on.
                padding: shouldShowMediaArea || shouldShowFallback ? "0.75rem 0.75rem 1rem 0.75rem" : "2.75rem 0.75rem 1rem 0.75rem",
                display: "flex",
                flexDirection: "column",
                gap: "0.75rem",

                // Grow to fill the bounded Card and scroll when content overflows.
                // `minHeight: 0` is required for a flex child to shrink below its
                // content size so the internal scrollbar actually engages.
                flex: "1 1 auto",
                minHeight: 0,

                // Enable scrolling when content overflows
                overflow: "auto",

                // Enhanced scrollbar styling
                "&::-webkit-scrollbar": {
                  width: "8px"
                },
                "&::-webkit-scrollbar-track": {
                  background: "rgba(0, 0, 0, 0.05)",
                  borderRadius: "4px"
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "rgba(0, 0, 0, 0.2)",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "rgba(0, 0, 0, 0.3)"
                  }
                }
              }}
            >
              {/* Title + description wrap beside actions, then use full width below (float clearfix). */}
              <Box
                sx={{
                  alignSelf: "stretch",
                  mt: dataItemEndpoint || activeEndpoint ? 0 : shouldShowMediaArea || shouldShowFallback ? 2 : 4,
                  // Contain floats; line boxes flow left of the floated toolbar until past its height, then full width.
                  "&::after": {
                    content: '""',
                    display: "table",
                    clear: "both"
                  }
                }}
              >
                <Box
                  sx={{
                    float: "right",
                    display: "flex",
                    flexDirection: "row",
                    gap: "0.25rem",
                    flexShrink: 0,
                    alignItems: "flex-start",
                    // Space between wrapped text and icons; pb so desc doesn’t crowd the row under icons.
                    ml: 0.75,
                    mb: 0.25
                  }}
                >
                  {shouldShowFindRelatedButton && (
                    <Tooltip title={findRelatedTooltip}>
                      <span style={{ display: "inline-flex", lineHeight: 0, verticalAlign: "top" }}>
                        <FlashyButton
                          variant='outlined'
                          color='inherit'
                          onClick={() => void handleRelatedLinkClick()}
                          disabled={relationshipsLoadInProgress}
                          aria-label='Find related items'
                          flashColors={["rgba(255, 255, 255, 0.55)", "rgba(186, 202, 222, 0.95)"]}
                          borderColor='rgba(252, 252, 253, 0.9)'
                          textColor='#FCFCFD'
                          flashOpacity={0.5}
                          flashWidth='75%'
                          pulseInterval={2}
                          flashInterval={2.2}
                          disableElevation
                          disableRipple
                          sx={{
                            minWidth: 40,
                            width: 40,
                            height: 40,
                            p: 0,
                            m: 0,
                            borderRadius: "50%",
                            borderWidth: "2px",
                            borderStyle: "solid",
                            fontSize: 0,
                            lineHeight: 0,
                            bgcolor: "transparent",
                            boxShadow: "none",
                            textTransform: "none",
                            "&:hover": {
                              backgroundColor: "rgba(255, 255, 255, 0.12)",
                              bgcolor: "rgba(255, 255, 255, 0.12)",
                              boxShadow: "none",
                              borderColor: "rgba(252, 252, 253, 0.95)"
                            },
                            "& .MuiButton-label": {
                              position: "relative",
                              zIndex: 2
                            }
                          }}
                        >
                          {relationshipsLoadInProgress ? (
                            <CircularProgress size={20} thickness={4} sx={{ color: "inherit" }} />
                          ) : (
                            <LinkOutlined sx={{ fontSize: "1.5rem" }} />
                          )}
                        </FlashyButton>
                      </span>
                    </Tooltip>
                  )}

                  {!isEditingDisabled && isEndpointEditable && canUpdate && (
                    <IconButton
                      onClick={handleEdit}
                      sx={{
                        color: "#FCFCFD",
                        bgcolor: "transparent",
                        width: 40,
                        height: 40,
                        "&:hover": {
                          bgcolor: "rgba(255, 255, 255, 0.25)"
                        }
                      }}
                    >
                      <CreateOutlined sx={{ fontSize: "1.5rem" }} />
                    </IconButton>
                  )}

                  <IconButton
                    onClick={() => {
                      setShowInfo(!showInfo);
                    }}
                    sx={{
                      color: "#FCFCFD",
                      bgcolor: showInfo ? "rgba(255, 255, 255, 0.25)" : "transparent",
                      width: 40,
                      height: 40,
                      "&:hover": {
                        bgcolor: "rgba(255, 255, 255, 0.25)"
                      }
                    }}
                  >
                    <InfoOutlinedIcon sx={{ fontSize: "1.5rem" }} />
                  </IconButton>

                  {/* Share layer — CM-table (spatial) */}
                  {isCmTableEndpoint(dataItemEndpoint || activeEndpoint) &&
                    (dataItemEndpoint || activeEndpoint)?.spatialType !== "none" && (
                      <LayerSharePopover
                        shareContext={{
                          kind: "cm-table",
                          endpoint: (dataItemEndpoint || activeEndpoint)!,
                          rowIds: item && "id" in item ? [(item as { id: number }).id] : undefined,
                          rowLabel: (item as { name?: string })?.name
                        }}
                      />
                    )}

                  {/* Share layer — spatial dataset */}
                  {(dataItemEndpoint || activeEndpoint)?.entityType === "spatial-dataset" && item && "id" in item && (
                    <LayerSharePopover
                      shareContext={{
                        kind: "spatial-dataset",
                        datasetId: (item as { id: number }).id,
                        datasetType: (item as { dataset_type?: SpatialDatasetType }).dataset_type,
                        datasetName: (item as { name?: string }).name ?? (dataItemEndpoint || activeEndpoint)?.name ?? "dataset"
                      }}
                    />
                  )}
                </Box>

                {/*
                 * Title and description are intentionally direct block-level siblings of the
                 * floated action column above (not wrapped in a flex container). A flex/overflow
                 * wrapper here would establish a new Block Formatting Context and sit beside the
                 * float, clamping the description to the left column. As block siblings, the
                 * title's short text wraps beside the float at the top, and the description
                 * starts beside the float but continues to full container width once it passes
                 * the float's height — which is what "take the full width" requires.
                 *
                 * The outer content-section Box (grandparent) already provides vertical scroll
                 * via overflow:auto, so the description doesn't need its own max-height/scroll.
                 */}
                <Tooltip title={entityName}>
                  <Typography
                    variant='h4'
                    component='div'
                    sx={{
                      m: 0,
                      mb: "0.375rem",
                      color: "#FCFCFD",
                      fontWeight: 700,
                      fontSize: "0.875rem",
                      lineHeight: 1.35,
                      letterSpacing: "0.0175rem",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflowWrap: "anywhere",
                      // Truncate long names to 2 lines; full text is available via the tooltip above.
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis"
                    }}
                  >
                    {entityName}
                  </Typography>
                </Tooltip>

                {(() => {
                  const descriptionText = getDataItemDescription(item, dataItemEndpoint || undefined);
                  return (
                    <Tooltip
                      title={
                        descriptionText ? (
                          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "0.25rem" }}>
                            <Box component='span' sx={{ flex: "1 1 auto", minWidth: 0 }}>
                              {descriptionText}
                            </Box>
                            <IconButton
                              size='small'
                              aria-label='Dismiss description'
                              onClick={() => setIsDescriptionTooltipOpen(false)}
                              sx={{
                                flexShrink: 0,
                                p: "0.125rem",
                                mt: "-0.125rem",
                                mr: "-0.125rem",
                                color: "inherit",
                                "&:hover": { bgcolor: "rgba(255, 255, 255, 0.2)" }
                              }}
                            >
                              <CloseIcon sx={{ fontSize: "0.875rem" }} />
                            </IconButton>
                          </Box>
                        ) : (
                          ""
                        )
                      }
                      open={!!descriptionText && isDescriptionTooltipOpen}
                      onOpen={() => setIsDescriptionTooltipOpen(true)}
                      onClose={() => setIsDescriptionTooltipOpen(false)}
                      disableHoverListener={!descriptionText}
                      placement='top'
                      arrow
                    >
                      <Typography
                        variant='body2'
                        component='div'
                        sx={{
                          color: "#D0D5DD",
                          lineHeight: 1.5,
                          fontSize: "0.8125rem",
                          minHeight: "2.4em",
                          wordBreak: "break-word",
                          overflowWrap: "anywhere",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          textOverflow: "ellipsis",

                          "&:empty::before": {
                            content: '"No description available"',
                            color: "#D0D5DD",
                            fontStyle: "italic"
                          }
                        }}
                      >
                        {descriptionText}
                      </Typography>
                    </Tooltip>
                  );
                })()}
              </Box>

              {/* Info Section  */}
              {showInfo && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.5rem"
                  }}
                >
                  <AdditionalDetailsPanel
                    key={String(item?.id ?? "details")}
                    item={item}
                    endpoint={dataItemEndpoint || undefined}
                    embeddedDark={!!popupMode}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.625rem",
                      color: UI_CONSTANTS.MUTED_TEXT,
                      lineHeight: 1.35,
                      opacity: 0.9,
                      px: 0.25
                    }}
                  >
                    Created {formatDate(initialItem?.created_at)} · Updated {formatDate(initialItem?.last_updated_at)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>

          {/* Related Items Ring/Cloud — shows after L3 relationships are fetched */}
          {!popupMode &&
            !hideRelatedItemsRing &&
            selectedEntity &&
            idsEqual(selectedEntity.id, item?.id) &&
            (() => {
              return (
                <Box
                  ref={ringContainerRef}
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    pointerEvents: "none", // Explicitly allow events to pass through to MediaDrawer
                    zIndex: 2, // Higher than main card to ensure ring items are clickable
                    transition: "all 0.3s ease-in-out" // Smooth transitions for repositioning
                  }}
                >
                  {!selectedEntity.isDataLoaded || isFetchingRelationships || loadingRels || loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "auto", // Loading indicator should be clickable
                        bgcolor: "rgba(255, 255, 255, 0.9)",
                        borderRadius: 2,
                        p: 3,
                        boxShadow: 3,
                        minWidth: 200,
                        textAlign: "center"
                      }}
                    >
                      <CircularProgress size={40} color='primary' />
                      <Typography variant='caption' sx={{ mt: 1, color: "text.secondary" }}>
                        {loadingProgress
                          ? `${loadingProgress.step} (${loadingProgress.current}/${loadingProgress.total})`
                          : loadingRels
                            ? "Finding related items..."
                            : "Loading relationships..."}
                      </Typography>
                      {/* Removed inline action here per request; on-demand button exists under title */}
                      {loadingProgress && loadingProgress.total > 1 && (
                        <Box sx={{ width: "100%", mt: 1, maxWidth: 200 }}>
                          <Box
                            sx={{
                              width: `${(loadingProgress.current / loadingProgress.total) * 100}%`,
                              height: 4,
                              backgroundColor: "primary.main",
                              borderRadius: 2,
                              transition: "width 0.3s ease-in-out"
                            }}
                          />
                        </Box>
                      )}
                    </Box>
                  ) : (
                    (() => {
                      const hasActualRelationships = selectedEntity.relationships && hasRelationships(selectedEntity.relationships);
                      const hasPotential = hasPotentialRelationships(selectedEntity);
                      const shouldRender =
                        ringContainerSize.width > 0 &&
                        ringContainerSize.height > 0 &&
                        selectedEntity.relationships &&
                        (hasActualRelationships || hasPotential);
                      console.log("[DataItemDetails] 🎯 RelatedItemsRing shouldRender:", {
                        shouldRender,
                        hasActualRelationships,
                        hasPotential,
                        containerReady: ringContainerSize.width > 0 && ringContainerSize.height > 0,
                        hasRelationshipsObject: !!selectedEntity.relationships
                      });

                      if (shouldRender) {
                        const transformedRelationships = transformToResponseShape(selectedEntity.relationships);
                        console.log("[DataItemDetails] 🎯 About to render RelatedItemsRing with:", {
                          containerWidth: ringContainerSize.width,
                          containerHeight: ringContainerSize.height,
                          transformedRelationships
                        });

                        return (
                          <ErrorBoundary>
                            <RelatedItemsRing
                              relationships={transformedRelationships}
                              onItemClick={handleRelatedItemClick}
                              containerWidth={ringContainerSize.width}
                              containerHeight={ringContainerSize.height}
                            />
                          </ErrorBoundary>
                        );
                      }

                      console.log("[DataItemDetails] 🚫 RelatedItemsRing NOT rendering due to failed conditions");
                      return null;
                    })()
                  )}
                </Box>
              );
            })()}

          {isPreviewOpen && previewItem && (
            <MediaViewer item={previewItem} open={isPreviewOpen} onClose={handleClosePreview} mode='dialog' />
          )}
        </Box>
      </ErrorBoundary>
    );
  }
);

export default DataItemDetails;
